import { Fragment, useState } from "react";
import { Link, useParams } from "react-router";
import {
  Grid,
  GridCellProps,
  GridColumn,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  Input,
  NumericTextBox,
  TextArea,
} from "@progress/kendo-react-inputs";
import { useDealReward } from "src/dm/hooks/useDealRewards";
import {
  useAddRewardBilling,
  useDeleteRewardBilling,
  useRewardBilling,
  useRewardDefaults,
  useUpdateRewardBilling,
} from "src/dm/hooks/useDealRewardSub";
import {
  DealRewardBilling,
  DealRewardBillingWriteRequest,
} from "src/dm/interfaces/DealRewardSub";
import { usePromoInfo } from "src/dm/hooks/useLookups";
import * as s from "./EditRewardBilling.css";

// Legacy EditRewardBilling.aspx renders a reward header strip at the top
// (Promo Code/Description, Reward Description, Start/End/Reward Amount),
// then a grid of billing rows with Add / Edit / Delete actions. The Add
// modal seeds Business Unit / Entry Type / AP Vendor / Sales Person from
// the cat-mgr DealMapping row (legacy `setDefaltSelection`). We mirror
// that here via the /reward/defaults endpoint.
//
// DealReward is 1:1 with Deal — the URL is `/dm/deals/:dealId/reward/billing`
// (no reward id). The hooks resolve the reward server-side via dealId.
//
// Layout (2026 cleanup): the header strip now uses the canonical
// `.formGrid` (180px / 1fr) so it lines up with EditReward / ViewReward.

const emptyForm = (): DealRewardBillingWriteRequest => ({
  businessUnit: null,
  entryType: null,
  salesPerson: null,
  customerId: null,
  addressSequence: null,
  billingAmount: null,
  billingPercent: null,
  invoiceDescription: null,
});

const fmtCurrency = (n: number | null | undefined): string =>
  n == null
    ? "—"
    : n.toLocaleString(undefined, { style: "currency", currency: "USD" });

const fmt = (v: unknown): string =>
  v == null || v === "" ? "—" : String(v);

// One label/value pair inside the header-strip grid. Fragment keeps the
// dt/dd as direct grid children so the 180/1fr columns line up.
const Row = ({ label, value }: { label: string; value: string }) => (
  <Fragment>
    <dt className={s.fieldLabel}>{label}</dt>
    <dd className={s.fieldValue}>{value}</dd>
  </Fragment>
);

// One label / input row inside the modal grid.
const FormRow = ({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Fragment>
    <label className={s.fieldLabel} htmlFor={htmlFor}>
      {label}
      {hint ? <span className={s.muted}> {hint}</span> : null}
    </label>
    <div className={s.inputCell}>{children}</div>
  </Fragment>
);

const EditRewardBilling = () => {
  const { dealId: dealIdParam } = useParams<{ dealId: string }>();
  const dealId = dealIdParam ? Number(dealIdParam) : 0;

  const rewardQ = useDealReward(dealId);
  const promoInfoQ = usePromoInfo(rewardQ.data?.promotionCode ?? undefined);
  const defaultsQ = useRewardDefaults(dealId);
  const { data, isLoading, isError, error } = useRewardBilling(dealId);
  const addM = useAddRewardBilling(dealId);
  const updateM = useUpdateRewardBilling(dealId);
  const removeM = useDeleteRewardBilling(dealId);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<DealRewardBillingWriteRequest>(emptyForm());

  // Reward's funding code dictates whether amount or percent is editable
  // (legacy `RewardIsPercent`).
  const rewardIsPercent = rewardQ.data?.vendorFundingCode === "P";

  const openAdd = () => {
    setEditingId(null);
    // Seed with cat-mgr defaults a la legacy setDefaltSelection.
    setForm({
      ...emptyForm(),
      businessUnit: defaultsQ.data?.businessUnit ?? null,
      entryType: defaultsQ.data?.rewardEntryType ?? null,
      salesPerson: defaultsQ.data?.supportTeamMember ?? null,
    });
    setShowModal(true);
  };

  const openEdit = (row: DealRewardBilling) => {
    setForm({
      businessUnit: row.businessUnit,
      entryType: row.entryType,
      salesPerson: row.salesPerson,
      customerId: row.customerId,
      addressSequence: row.addressSequence,
      billingAmount: row.billingAmount,
      billingPercent: row.billingPercent,
      invoiceDescription: row.invoiceDescription,
    });
    setEditingId(row.rewardBillingId);
    setShowModal(true);
  };

  const validateBeforeSave = (): string | null => {
    const hasAmt = form.billingAmount != null;
    const hasPct = form.billingPercent != null;
    if (rewardIsPercent && hasAmt)
      return "Reward funding type is %; please enter a Billing Percent rather than an Amount.";
    if (!rewardIsPercent && hasPct)
      return "Reward funding type is $; please enter a Billing Amount rather than a Percent.";
    return null;
  };

  const handleSave = async () => {
    const v = validateBeforeSave();
    if (v) {
      alert(v);
      return;
    }
    if (editingId == null) {
      await addM.mutateAsync(form);
    } else {
      await updateM.mutateAsync({ rewardBillingId: editingId, body: form });
    }
    setShowModal(false);
  };

  const setField = <K extends keyof DealRewardBillingWriteRequest>(
    key: K,
    value: DealRewardBillingWriteRequest[K]
  ) => setForm((p) => ({ ...p, [key]: value }));

  const Actions = (props: GridCellProps) => {
    const row = props.dataItem as DealRewardBilling;
    return (
      <td>
        <Button size="small" fillMode="flat" onClick={() => openEdit(row)}>
          Edit
        </Button>
        <Button
          size="small"
          fillMode="flat"
          themeColor="error"
          onClick={() => {
            if (confirm(`Delete billing #${row.rewardBillingId}?`))
              removeM.mutate(row.rewardBillingId);
          }}
        >
          Delete
        </Button>
      </td>
    );
  };

  // No reward configured → friendly notice with a link back to the editor.
  if (!rewardQ.isLoading && !rewardQ.data) {
    return (
      <div className={s.wrap}>
        <Link to={`/dm/deals/${dealId}/reward`} className={s.backLink}>
          ← Back to reward
        </Link>
        <h2 className={s.titleText} style={{ margin: "1rem 0" }}>
          No reward configured
        </h2>
        <p style={{ color: "#475569" }}>
          This deal does not have a reward configured yet. Create one from
          the reward editor before adding billing entries.
        </p>
        <Link to={`/dm/deals/${dealId}/reward`}>
          <Button themeColor="primary">Go to reward editor</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className={s.wrap}>
      <Link to={`/dm/deals/${dealId}/reward`} className={s.backLink}>
        ← Back to reward
      </Link>

      <div className={s.titleRow}>
        <h2 className={s.titleText}>Reward Billing</h2>
        <div className={s.titleActions}>
          <Link to={`/dm/deals/${dealId}/reward`}>
            <Button>Reward Header</Button>
          </Link>
          <Link to={`/dm/deals/${dealId}/reward/items`}>
            <Button>Reward Items</Button>
          </Link>
        </div>
      </div>

      {/* Reward header strip — mirrors legacy `rwrdData` UL block. Uses
          the same 180/1fr formGrid as EditReward so the two pages share
          a visual rhythm. */}
      {rewardQ.data && (
        <section className={s.card}>
          <h3 className={s.sectionTitle}>Reward Header Information</h3>
          <dl className={s.formGrid}>
            <Row label="Promo Code" value={fmt(rewardQ.data.promotionCode)} />
            <Row
              label="Promo Description"
              value={fmt(promoInfoQ.data?.description)}
            />
            <Row
              label="Reward Description"
              value={fmt(rewardQ.data.description)}
            />
            <Row
              label="Reward Amount"
              value={fmtCurrency(rewardQ.data.rewardAmount)}
            />
            <Row
              label="Funding Type"
              value={rewardIsPercent ? "Percent" : "Amount"}
            />
            <Row
              label="Auto-Billed"
              value={rewardQ.data.autoBilling ? "Yes" : "No"}
            />
          </dl>
          {!rewardQ.data.autoBilling && (
            <p className={s.warnBanner}>
              Note: this reward is not flagged as Auto-Billed — legacy
              redirects away from this page in that case.
            </p>
          )}
        </section>
      )}

      <section className={s.card}>
        <div className={s.header}>
          <h3 className={s.sectionTitle} style={{ borderBottom: "none", paddingBottom: 0, margin: 0 }}>
            Billing entries ({data?.length ?? 0})
          </h3>
          <Button themeColor="primary" onClick={openAdd}>
            Add billing
          </Button>
        </div>

        {isLoading && <p className={s.empty}>Loading…</p>}
        {isError && (
          <p className={s.errorText}>
            Could not load:{" "}
            {error instanceof Error ? error.message : "unknown"}
          </p>
        )}
        {!isLoading && (data?.length ?? 0) === 0 && (
          <p className={s.empty}>No billing entries.</p>
        )}

        {(data?.length ?? 0) > 0 && (
          <Grid data={data}>
            <GridColumn field="rewardBillingId" title="#" width="80px" />
            <GridColumn
              field="businessUnit"
              title="Business Unit"
              width="130px"
            />
            <GridColumn field="customerId" title="AP Vendor" width="120px" />
            <GridColumn
              field="addressSequence"
              title="Address Seq"
              width="120px"
            />
            <GridColumn field="entryType" title="Entry Type" width="120px" />
            <GridColumn
              field="salesPerson"
              title="Category Mgr"
              width="130px"
            />
            <GridColumn
              title="Amount / Percent"
              width="140px"
              cells={{
                data: (props: GridCellProps) => {
                  const row = props.dataItem as DealRewardBilling;
                  return (
                    <td className="numeric">
                      {row.billingAmount != null
                        ? fmtCurrency(row.billingAmount)
                        : row.billingPercent != null
                          ? `${row.billingPercent}%`
                          : "—"}
                    </td>
                  );
                },
              }}
            />
            <GridColumn
              field="invoiceDescription"
              title="Invoice Description"
            />
            <GridColumn title="" width="160px" cells={{ data: Actions }} />
          </Grid>
        )}
      </section>

      {showModal && (
        <Dialog
          title={editingId == null ? "Add billing" : `Edit billing #${editingId}`}
          onClose={() => setShowModal(false)}
        >
          <div className={s.dialogGrid}>
            <FormRow label="Business Unit" htmlFor="b-bu">
              <Input
                id="b-bu"
                style={{ width: "100%" }}
                value={form.businessUnit ?? ""}
                onChange={(e) =>
                  setField(
                    "businessUnit",
                    String(e.target.value ?? "") || null
                  )
                }
              />
            </FormRow>
            <FormRow label="AP Vendor (Customer Id)" htmlFor="b-cust">
              <Input
                id="b-cust"
                style={{ width: "100%" }}
                value={form.customerId ?? ""}
                onChange={(e) =>
                  setField("customerId", String(e.target.value ?? "") || null)
                }
              />
            </FormRow>
            <FormRow label="Address Sequence">
              <NumericTextBox
                width="100%"
                value={form.addressSequence ?? null}
                onChange={(e) =>
                  setField("addressSequence", e.value ?? null)
                }
              />
            </FormRow>
            <FormRow label="Entry Type" htmlFor="b-et">
              <Input
                id="b-et"
                style={{ width: "100%" }}
                value={form.entryType ?? ""}
                onChange={(e) =>
                  setField("entryType", String(e.target.value ?? "") || null)
                }
              />
            </FormRow>
            <FormRow label="Category Manager (sales person)" htmlFor="b-sp">
              <Input
                id="b-sp"
                style={{ width: "100%" }}
                value={form.salesPerson ?? ""}
                onChange={(e) =>
                  setField("salesPerson", String(e.target.value ?? "") || null)
                }
              />
            </FormRow>
            <FormRow
              label="Billing Amount"
              hint={rewardIsPercent ? "(disabled — reward is %)" : undefined}
            >
              <NumericTextBox
                format="c2"
                width="100%"
                disabled={rewardIsPercent}
                value={form.billingAmount ?? null}
                onChange={(e) =>
                  setField("billingAmount", e.value ?? null)
                }
              />
            </FormRow>
            <FormRow
              label="Billing Percent"
              hint={!rewardIsPercent ? "(disabled — reward is $)" : undefined}
            >
              <NumericTextBox
                width="100%"
                disabled={!rewardIsPercent}
                value={form.billingPercent ?? null}
                onChange={(e) =>
                  setField("billingPercent", e.value ?? null)
                }
              />
            </FormRow>
            <FormRow label="Invoice Description (200)" htmlFor="b-inv">
              <TextArea
                id="b-inv"
                maxLength={200}
                rows={3}
                style={{ width: "100%" }}
                value={form.invoiceDescription ?? ""}
                onChange={(e) =>
                  setField(
                    "invoiceDescription",
                    String(e.value ?? "") || null
                  )
                }
              />
            </FormRow>
          </div>
          <DialogActionsBar>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button
              themeColor="primary"
              onClick={handleSave}
              disabled={addM.isPending || updateM.isPending}
            >
              {addM.isPending || updateM.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default EditRewardBilling;
